<template>
  <div class="panel">
    <header class="page-header">
      <div>
        <h1>字体库</h1>
        <p>全平台共享字体库 · 共 {{ filteredFonts.length }} 个字体<template v-if="!canDeleteFont"> · 删除需平台管理员</template></p>
      </div>
      <label
        class="btn-primary"
        :class="{ disabled: uploading }"
        title="可按 Command / Shift 一次选择多个字体"
      >
        {{ uploading ? `正在读取 ${uploadingCount} 个…` : '批量上传字体' }}
        <input type="file" accept=".ttf,.otf,font/ttf,font/otf" multiple hidden @change="onUpload" />
      </label>
    </header>

    <!-- 字体检索工具栏 -->
    <div class="fonts-toolbar">
      <div class="search-wrap">
        <input v-model.trim="searchKeyword" type="search" placeholder="搜索字体名称或文件名" aria-label="搜索字体" />
      </div>
      <span class="toolbar-count">共 {{ filteredFonts.length }} 个字体</span>
    </div>

    <div class="font-grid">
      <label
        class="upload-tile"
        :class="{ 'is-dragging': dragging }"
        @dragenter.prevent="dragging = true"
        @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
        @drop.prevent="onDrop"
      >
        <span class="plus">+</span>
        <strong>批量上传字体</strong>
        <span>可多选或拖入多个 TTF、OTF 文件</span>
        <input type="file" accept=".ttf,.otf,font/ttf,font/otf" multiple hidden @change="onUpload" />
      </label>
      <div
        v-for="f in filteredFonts"
        :key="f.id"
        class="font-card"
        :class="{ 'is-manageable': canDeleteFont && f.source !== 'system' }"
      >
        <div class="font-preview" :style="{ fontFamily: previewFamily(f) }">
          <span class="font-preview-main" :title="previewText(f)">{{ previewText(f) }}</span>
          <span class="font-preview-sub">Aa Bb 123</span>
          <div v-if="canDeleteFont && f.source !== 'system'" class="card-actions">
            <button type="button" class="btn-action danger" @click.stop="removeFont(f.id)">删除</button>
          </div>
        </div>
        <div class="font-meta">
          <div class="font-title-row">
            <strong :title="displayFontName(f)">{{ displayFontName(f) }}</strong>
            <span class="font-source-tag" :class="f.source">{{ f.source === 'system' ? '系统' : '云端' }}</span>
          </div>
          <span :title="f.fileName" class="font-sub-meta">{{ f.fileName }}</span>
          <span v-if="f.source !== 'system'" :title="fontSpec(f)" class="font-sub-meta">{{ fontSpec(f) }}</span>
          <span :title="fontUploadMeta(f)" class="font-sub-meta">{{ fontUploadMeta(f) }}</span>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="pendingDelete" class="font-dialog-overlay" @mousedown.self="closeDeleteDialog">
        <section class="font-dialog" role="dialog" aria-modal="true" aria-labelledby="font-delete-title">
          <button type="button" class="font-dialog-close" aria-label="关闭" @click="closeDeleteDialog">×</button>
          <h3 id="font-delete-title">删除字体</h3>
          <p>确定删除“{{ pendingDelete.name }}”吗？</p>
          <template v-if="pendingUsage.length">
            <div class="usage-alert">
              <strong>该字体正被 {{ usageDesignCount }} 项内容中的 {{ usageLayerCount }} 个文字图层使用</strong>
              <span>{{ usageLabels }}</span>
            </div>
            <label class="replacement-field">
              <span>删除前选择替代字体</span>
              <select v-model="replacementId">
                <option value="" disabled>请选择替代字体</option>
                <option v-for="font in replacementOptions" :key="font.id" :value="font.id">
                  {{ font.name }}{{ font.subfamily ? ` · ${font.subfamily}` : '' }}
                </option>
              </select>
            </label>
          </template>
          <p v-else>删除后，该字体将从当前浏览器的字体库和编辑器中移除。</p>
          <footer class="font-dialog-actions">
            <button type="button" @click="closeDeleteDialog">取消</button>
            <button
              type="button"
              class="danger"
              :disabled="pendingUsage.length > 0 && !replacementId"
              @click="confirmRemoveFont"
            >确认删除</button>
          </footer>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import { formatPlatformFontName } from '@/utils/fontMapper';

const {
  platformFonts,
  addPlatformFontFiles,
  removePlatformFont,
  getPlatformFontUsage,
  showToast,
  perms,
  fontApiAvailable,
} = usePlatformStore();
const fonts = computed(() => platformFonts.value);
const useFontApi = import.meta.env.VITE_USE_FONT_API !== 'false';
const fontApiOnline = computed(() => useFontApi && fontApiAvailable.value);
const canDeleteFont = computed(() => !!perms.value.deleteFonts);
const pendingDelete = ref(null);
const pendingUsage = ref([]);
const replacementId = ref('');
const uploading = ref(false);
const uploadingCount = ref(0);
const dragging = ref(false);
const searchKeyword = ref('');

const filteredFonts = computed(() => {
  if (!searchKeyword.value) return fonts.value;
  const q = searchKeyword.value.toLowerCase();
  return fonts.value.filter(f =>
    (f.name && f.name.toLowerCase().includes(q))
    || (f.fileName && f.fileName.toLowerCase().includes(q))
    || (f.family && f.family.toLowerCase().includes(q))
  );
});

const replacementOptions = computed(() =>
  fonts.value.filter(font => font.id !== pendingDelete.value?.id)
);
const usageDesignCount = computed(() => pendingUsage.value.length);
const usageLayerCount = computed(() =>
  pendingUsage.value.reduce((total, usage) => total + usage.count, 0)
);
const usageLabels = computed(() =>
  pendingUsage.value.slice(0, 3).map(usage => usage.label).join('、')
  + (pendingUsage.value.length > 3 ? ` 等 ${pendingUsage.value.length} 项内容` : '')
);

function displayFontName(font) {
  if (font.source === 'system') return font.name;
  return formatPlatformFontName(font);
}

function previewFamily(f) {
  const fallbacks = {
    'Microsoft YaHei': '"Microsoft YaHei", "PingFang SC", sans-serif',
    SimSun: 'SimSun, "Songti SC", serif',
    Arial: 'Arial, sans-serif',
    Helvetica: 'Helvetica, Arial, sans-serif',
    'Noto Sans SC': '"Noto Sans SC", "PingFang SC", sans-serif',
  };
  return fallbacks[f.family] || `"${f.family}", "Microsoft YaHei", sans-serif`;
}

function previewText(f) {
  return displayFontName(f);
}

function fontSpec(font) {
  return `${font.subfamily || 'Regular'} · 字重 ${font.weight || 400} · ${formatSize(font.size)}`;
}

function fontUploadMeta(font) {
  return `${font.uploadedAt || '时间未知'} · ${font.source === 'system' ? '系统预设' : '已上传'}`;
}

async function onUpload(e) {
  const files = [...(e.target.files || [])];
  e.target.value = '';
  await uploadFiles(files);
}

async function onDrop(e) {
  dragging.value = false;
  const files = [...(e.dataTransfer?.files || [])].filter(file => /\.(ttf|otf)$/i.test(file.name || ''));
  if (!files.length) {
    showToast('请拖入 TTF 或 OTF 字体文件', 'warn');
    return;
  }
  await uploadFiles(files);
}

async function uploadFiles(files) {
  if (!files.length || uploading.value) return;
  uploading.value = true;
  uploadingCount.value = files.length;
  try {
    const result = await addPlatformFontFiles(files);
    if (result.added.length) {
      showToast(`已上传 ${result.added.length} 个字体，编辑器已同步`, 'success');
    } else if (result.duplicates.length) {
      showToast(result.duplicates[0].reason, 'warn');
    } else if (result.errors.length) {
      showToast(`${result.errors[0].fileName}：${result.errors[0].reason}`, 'error');
    }
    if (result.added.length && (result.duplicates.length || result.errors.length)) {
      const skipped = result.duplicates.length + result.errors.length;
      setTimeout(() => showToast(`另有 ${skipped} 个文件未导入，请检查格式或重复项`, 'warn'), 400);
    }
  } catch (err) {
    showToast(err?.message || '上传字体失败', 'error');
  } finally {
    uploading.value = false;
    uploadingCount.value = 0;
  }
}

function removeFont(id) {
  pendingDelete.value = fonts.value.find(font => font.id === id) || null;
  pendingUsage.value = pendingDelete.value ? getPlatformFontUsage(id) : [];
  replacementId.value = '';
}

function closeDeleteDialog() {
  pendingDelete.value = null;
  pendingUsage.value = [];
  replacementId.value = '';
}

async function confirmRemoveFont() {
  if (!pendingDelete.value) return;
  const result = await removePlatformFont(pendingDelete.value.id, replacementId.value);
  if (!result.ok) {
    showToast(result.reason === 'in-use' ? '请先选择替代字体' : '字体删除失败', 'warn');
    return;
  }
  closeDeleteDialog();
  showToast(result.replaced ? `字体已删除，并替换 ${result.replaced} 个文字图层` : '字体已删除', 'success');
}

function formatSize(bytes) {
  if (!Number(bytes)) return '—';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
</script>

<style scoped>
.panel { padding: 24px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.page-header h1 { font-size: 22px; font-weight: 700; }
.page-header p { font-size: 13px; color: var(--text-muted); margin-top: 4px; }
.btn-primary { padding: 8px 18px; border-radius: var(--radius-round); background: #2c2c2c; color: #fff; font-size: 13px; cursor: pointer; }
.btn-primary.disabled { opacity: 0.55; pointer-events: none; }

.fonts-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.fonts-toolbar input {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease;
}
.fonts-toolbar input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
}
.search-wrap input { width: 280px; }
.toolbar-count { margin-left: auto; color: var(--text-muted); font-size: 13px; }

.font-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
.upload-tile, .font-card { border-radius: 12px; border: 1px solid var(--border-color, #e2e8f0); background: #fff; min-height: 150px; position: relative; }
.upload-tile { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; border-style: dashed; cursor: pointer; color: var(--text-muted); font-size: 12px; }
.upload-tile:hover, .upload-tile.is-dragging { border-color: var(--accent-green); color: var(--accent-green); background: #f5f3ff; }
.upload-tile strong { font-size: 13px; color: var(--text-dark); }
.plus { font-size: 28px; font-weight: 300; }
.font-card { min-width: 0; padding: 14px; overflow: hidden; transition: all 0.2s ease; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04); }
.font-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 10px 20px -3px rgba(15, 23, 42, 0.08);
}
.font-preview {
  position: relative;
  height: 72px; display: flex; flex-direction: column; justify-content: center; gap: 2px;
  border-bottom: 1px solid #f1f5f9; margin-bottom: 10px; color: #1f1f1f; overflow: hidden;
}
.font-preview-main { font-size: 24px; line-height: 1.15; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.font-preview-sub { font-size: 12px; line-height: 1.2; color: #94a3b8; letter-spacing: 0.02em; }
.font-meta { display: flex; min-width: 0; flex-direction: column; gap: 3px; font-size: 11px; color: var(--text-muted); }
.font-title-row { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.font-title-row strong { font-size: 13px; color: var(--text-dark, #0f172a); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.font-source-tag { font-size: 10px; padding: 1px 6px; border-radius: 4px; font-weight: 600; flex-shrink: 0; }
.font-source-tag.system { background: #f1f5f9; color: #64748b; }
.font-source-tag.cloud, .font-source-tag.user { background: var(--color-info-light); color: var(--color-info-text); }
.font-sub-meta { color: #64748b; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.card-actions {
  position: absolute; top: 6px; right: 6px;
  display: flex; gap: 4px; z-index: 2;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.18s ease, transform 0.18s ease;
  pointer-events: none;
}
.font-card.is-manageable:hover .card-actions {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
.btn-action {
  padding: 2px 8px; font-size: 11px; border-radius: 999px;
  border: 1px solid var(--border-color, #e2e8f0); background: #fff; color: var(--text-dark);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  cursor: pointer;
}
.btn-action.danger { border-color: #ffa39e; background: #fff1f0; color: #cf1322; }
.btn-action:hover { filter: brightness(0.97); }
.btn-sm { padding: 4px 10px; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; font-size: 12px; cursor: pointer; }
.font-dialog-overlay {
  position: fixed; inset: 0; z-index: 12000; display: grid; place-items: center; padding: 20px;
  background: rgba(15, 23, 42, 0.46); backdrop-filter: blur(3px);
}
.font-dialog {
  position: relative; width: min(460px, 100%); padding: 26px;
  border-radius: 18px; background: #fff; box-shadow: 0 28px 80px rgba(15, 23, 42, 0.24);
}
.font-dialog h3 { margin: 0; font-size: 19px; color: #0f172a; }
.font-dialog p { margin: 10px 0 0; color: #64748b; font-size: 13px; line-height: 1.6; }
.font-dialog-close {
  position: absolute; top: 14px; right: 14px; width: 32px; height: 32px;
  border: 0; border-radius: 9px; background: #f8fafc; color: #64748b; font-size: 20px; cursor: pointer;
}
.usage-alert {
  display: grid; gap: 5px; margin-top: 16px; padding: 12px 14px;
  border: 1px solid #fed7aa; border-radius: 10px; background: #fff7ed; color: #9a3412; font-size: 12px;
}
.usage-alert span { color: #c2410c; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.replacement-field { display: grid; gap: 7px; margin-top: 16px; color: #475569; font-size: 12px; font-weight: 600; }
.replacement-field select {
  width: 100%; height: 42px; padding: 0 12px; border: 1px solid #dbe3ed;
  border-radius: 10px; background: #fff; color: #0f172a; outline: none;
}
.replacement-field select:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12); }
.font-dialog-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 22px; }
.font-dialog-actions button {
  min-width: 86px; height: 38px; padding: 0 17px; border: 1px solid #dbe3ed;
  border-radius: 999px; background: #fff; color: #334155; font-size: 13px; font-weight: 600; cursor: pointer;
}
.font-dialog-actions button.danger { border-color: #dc2626; background: #dc2626; color: #fff; }
.font-dialog-actions button:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
